import { ConfirmationModalBase } from "@/components/common/modals";
import { useUpdateNode } from "@/workflow_api/services/nodes.api";
import { useUIStore } from "@/workflow_api/store/uiStore";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import {
  NodeTypes,
  type NodeData,
  type RefName,
} from "@/workflow_api/types/workflow";
import { useMemo, useState } from "react";
import { useNodeNameValidation } from "@/workflow_api/hooks/useNodeNameValidation";
import FullModal from "../../full-modal";
import ThreeColumnLayout from "@/components/common/three-column-layout";
import Variables from "@/workflow_api/components/variables";
import { InputOutput } from "../../InputOutput";
import CorrectionContent from "./content";
import type { Node } from "@xyflow/react";
import { getSerializedTimerInputs } from "../timer/serialization";
import type { TimerBlockValues } from "../timer/types";
import { getDeserializedTimerInputs } from "../timer/serialization/deserialization";
import type { GroupType } from "@/types/entities/approval-groups";

interface Props {
  initialTitle?: string;
  formNodes: Node[];
}

export const Wrapper = ({
  initialTitle = "Revisión de formularios",
  formNodes,
}: Props) => {
  const [title, setTitle] = useState<string>(initialTitle);
  const [showNodeTitleValidation, setShowNodeTitleValidation] = useState(false);
  const { flowId, getNodeById } = useWorkflowStore();
  const { mutateAsync: updateNode, isPending: isLoadingUpdate } =
    useUpdateNode(flowId);

  const [modalState, setModalState] = useState<"idle" | "close-warning">(
    "idle",
  );
  const {
    nodeDetailsOpen,
    setNodeDetailsOpen,
    setSelectedNodeId,
    selectedNodeId,
  } = useUIStore();

  const { getNameValidationError, isNameValid } =
    useNodeNameValidation(selectedNodeId);

  const [internalTimer, setInternalTimer] = useState<TimerBlockValues>({
    hasTimer: false,
    isComplete: false,
  });
  const [externalTimer, setExternalTimer] = useState<TimerBlockValues>({
    hasTimer: false,
    isComplete: false,
  });
  const [selectedGroupsList, setSelectedGroupsList] = useState<GroupType[]>([]);
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;

  const [requiredLeadApprove, setRequiredLeadApprove] = useState<boolean>(
    () => {
      const groupInputs = nodeData?.inputs?.filter(
        (input) =>
          input.name === "requires_leader_approval" && input.type === "BOOLEAN",
      )[0];

      if (groupInputs) return !!groupInputs.value;

      return false;
    },
  );

  const [selectedGroups, setSelectedGroups] = useState<string[]>(() => {
    const groupInputs = nodeData?.inputs?.filter(
      (input) => input.name === "group" && input.type === "STRING",
    );
    if (groupInputs && groupInputs.length > 0) {
      return groupInputs
        .map((input) => input.value as string)
        .filter((value) => typeof value === "string" && value.length > 0);
    }
    return [];
  });

  const memoInitialValues = useMemo(() => {
    const { externalData, internalData } = getDeserializedTimerInputs(nodeData);
    return {
      externalData,
      internalData,
    };
  }, [nodeData]);

  const [limit, setLimit] = useState(() => {
    const limitInput = nodeData?.inputs?.find(
      (input) => input.name === "limit",
    );
    if (limitInput?.value && typeof !isNaN(Number(limitInput.value))) {
      return limitInput?.value?.toString();
    }
    return "";
  });
  const [selectedForm, setSelectedForm] = useState<string>(() => {
    const formInput = nodeData?.inputs?.find((input) => input.name === "form");
    if (formInput && typeof formInput.value === "string") {
      return formInput.value;
    }
    return "";
  });

  const onSave = async () => {
    if (!selectedNodeId) return;
    if (!selectedNode) return;
    // update node with serialized inputs
    const inputs = getSerializedTimerInputs(
      [
        {
          name: "limit",
          type: "NUMBER",
          value: limit === "" ? null : parseInt(limit, 10),
        },
        {
          name: "form",
          type: "FORM",
          value: selectedForm,
        },
      ],
      internalTimer.values,
      externalTimer.values,
    );

    inputs.concat([
      {
        name: "_dynamic_validation_bypass",
        value: null,
        type: "CONDITION",
      },
    ]);

    inputs.push({
      name: "requires_leader_approval",
      type: "BOOLEAN",
      value: requiredLeadApprove,
    });

    // Agregar un input por cada grupo seleccionado
    selectedGroups.forEach((groupId) => {
      inputs.push({
        name: "group",
        value: groupId,
        type: "STRING",
      });
    });

    await updateNode({
      node_id: selectedNodeId,
      node: {
        flow_id: flowId,
        ref_name: nodeData.type as RefName,
        display_name: title,
        order: nodeData.order,
        position_x: selectedNode.position.x,
        position_y: selectedNode.position.y,
        type: nodeData.node_type,
        manual_continue: manualConfirmation,
        inputs,
      },
    });
    setNodeDetailsOpen(false);
    setSelectedNodeId();
  };

  const isFormComplete = useMemo(() => {
    if (
      !internalTimer.hasTimer &&
      !externalTimer.hasTimer &&
      selectedGroupsList.length > 0
    ) {
      return true;
    }
    // If any timer is added/visible, at least one needs to be complete
    const externalValid = !externalTimer.hasTimer || externalTimer.isComplete;
    const internalValid = !internalTimer.hasTimer || internalTimer.isComplete;
    return externalValid && internalValid && selectedGroupsList.length > 0;
  }, [
    internalTimer.hasTimer,
    externalTimer.hasTimer,
    internalTimer.isComplete,
    externalTimer.isComplete,
    selectedGroupsList,
  ]);

  const isNodeValid = selectedForm !== "" && isFormComplete;

  const [manualConfirmation, setManualConfirmation] = useState(() => {
    return nodeData?.manual_continue ?? false;
  });

  return (
    <>
      <ConfirmationModalBase
        isOpen={modalState === "close-warning"}
        acceptText="Salir sin guardar"
        cancelText="Cancelar"
        description="¿Estás seguro de que deseas salir? Todos los cambios que hayas realizado se perderán"
        title="Salir sin guardar"
        acceptVariant="danger-secondary"
        isLoading={false}
        onAccept={() => {
          setNodeDetailsOpen(false);
          setSelectedNodeId();
          setModalState("idle");
        }}
        onClose={() => {
          setModalState("idle");
        }}
      />
      <FullModal
        isOpen={nodeDetailsOpen}
        isSaveValid={isNodeValid && isNameValid(title)}
        onClose={() => {
          setModalState("close-warning");
        }}
        onSave={() => {
          setShowNodeTitleValidation(true);
          if (!isNameValid(title)) return;
          onSave();
        }}
        isLoading={isLoadingUpdate}
      >
        <ThreeColumnLayout
          className="overflow-auto"
          leftHeader={{
            icon: "arrow-right-to-bracket",
            title: "Inputs",
            color: "neutral",
          }}
          centerHeader={{
            icon: "file-edit",
            title: title,
            color: "action",
            editable: true,
            onTitleChange: (newTitle: string) => {
              setTitle(newTitle);
              if (newTitle.trim() === "") {
                setShowNodeTitleValidation(true);
              }
            },
            onTitleBlur: () => setShowNodeTitleValidation(true),
            validationError: getNameValidationError(title),
            showValidation: showNodeTitleValidation,
            showManualConfirmation: true,
            manualConfirmation: manualConfirmation,
            onManualConfirmationChange: (checked) => {
              console.log("Confirmación manual (Revisión):", checked);
              setManualConfirmation(checked);
            },
          }}
          rightHeader={{
            icon: "arrow-right-from-bracket",
            title: "Outputs",
            color: "neutral",
          }}
          leftContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                <Variables sourceNodeId={selectedNodeId!} />
              </div>
            </>
          }
          centerContent={
            <CorrectionContent
              nodes={formNodes}
              limit={limit}
              setLimit={setLimit}
              selectedForm={selectedForm}
              setSelectedForm={setSelectedForm}
              memoInitialValues={memoInitialValues}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              setExternalTimer={setExternalTimer}
              setInternalTimer={setInternalTimer}
              selectedGroupsList={selectedGroupsList}
              setSelectedGroupsList={setSelectedGroupsList}
              setRequiredLeadApprove={setRequiredLeadApprove}
              requiredLeadApprove={requiredLeadApprove}
            />
          }
          rightContent={
            <>
              <div className="bg-neutral-foreground overflow-auto divide-y divide-(--border-neutral-quiet)">
                <InputOutput
                  nodeType={NodeTypes.CORRECTION}
                  showSteps={false}
                  variableName={title || undefined}
                  nodeLabel={title}
                  variableType={"boolean"}
                />
              </div>
            </>
          }
        />
      </FullModal>
    </>
  );
};

export const CorrectionDetails = () => {
  const { getNodeById, getFormsAvailableForCorrection } = useWorkflowStore();
  const { selectedNodeId } = useUIStore();
  const selectedNode = getNodeById(selectedNodeId || "");
  const nodeData = selectedNode?.data as unknown as NodeData;
  const nodes = getFormsAvailableForCorrection(selectedNodeId!);
  return <Wrapper initialTitle={nodeData.displayName} formNodes={nodes} />;
};
